import { Session } from './session';
import { ListEvent, ModalEvent, RecipeEvent } from './events';
import { useFolder } from './shared/hooks/useFolder';
import { waitForStore, getStoreIfReady } from './store/utils/waitForStore';
import { getUserFolderAmount } from './store/utils/getUserFolderAmount';
import { getUserRecipeAmount } from './store/utils/getUserRecipeAmount';
import { getUserFolderRecipeAmount } from './store/utils/getUserFolderRecipeAmount';
import { checkAuthAndMaybeRedirect, isUserAuthenticated } from './session/utils';
import {
	findListItemClosestHeading,
	pushEditFolderClick,
	pushEditRecipesClick,
	pushKeittokirjaCreateFolder,
	pushKeittokirjaCreateFolderClick,
	pushKeittokirjaFindRecipesClick,
	pushKeittokirjaSaveNewFolder,
	pushKeittokirjaSaveRecipeFromList,
	pushKeittokirjaSaveRecipeFromRecipe,
	pushRemoveFolderClick,
} from './analytics';

import { createStore } from './shared/vendor/zustand';
import { DEFAULT_QUERY_CLIENT_CONFIG } from './shared/constants/react-query';
import {
	useQuery,
	useMutation,
	useQueryClient,
	QueryClient,
	QueryClientProvider,
} from './shared/vendor/react-query';
import type { ValioAppGlobal } from './shared/types/global';

/**
 * Initialize the global valioApp object and attach event classes
 * Note: window.valioApp.config is pre-populated by WordPress via wp_localize_script
 */
if (typeof window !== 'undefined') {
	// Initialize valioApp if it doesn't exist
	window.valioApp = window.valioApp || ({} as ValioAppGlobal);

	// Initialize valioExternals if it doesn't exist
	window.valioExternals = window.valioExternals || {};

	/**
	 * Attach the session instance
	 */
	window.valioApp.session = new Session();

	/**
	 * Initialize the events object with instantiated event classes
	 * These are singletons that can be used across the application
	 */
	window.valioApp.events = {
		ListEvent: new ListEvent(),
		ModalEvent: new ModalEvent(),
		RecipeEvent: new RecipeEvent(),
	};

	/**
	 * Initialize the hooks object with custom hooks
	 */
	window.valioApp.hooks = {
		useFolder,
	};

	/**
	 * Initialize the utils object with utility functions
	 */
	window.valioApp.utils = {
		waitForStore,
		getStoreIfReady,
		checkAuthAndMaybeRedirect,
		isUserAuthenticated,
		getUserFolderAmount,
		getUserFolderRecipeAmount,
		getUserRecipeAmount,
	};

	/**
	 * Initialize the analytics object with analytics tracking functions
	 */
	window.valioApp.analytics = {
		findListItemClosestHeading,
		pushEditFolderClick,
		pushEditRecipesClick,
		pushKeittokirjaCreateFolder,
		pushKeittokirjaCreateFolderClick,
		pushKeittokirjaFindRecipesClick,
		pushKeittokirjaSaveNewFolder,
		pushKeittokirjaSaveRecipeFromList,
		pushKeittokirjaSaveRecipeFromRecipe,
		pushRemoveFolderClick,
	};

	/**
	 * Attach zustand's createStore function for creating stores in other modules
	 */
	window.valioExternals.zustand = {
		createStore,
	};

	/**
	 * Attach React Query and its functions for use in other modules
	 */
	window.valioExternals['react-query'] = {
		defaultQueryClient: new QueryClient(DEFAULT_QUERY_CLIENT_CONFIG),
		QueryClient,
		QueryClientProvider,
		useQuery,
		useMutation,
		useQueryClient,
	};

	/**
	 * Initialize the store object synchronously
	 * Import store AFTER window.valioExternals.zustand is initialized to avoid circular dependency
	 * Note: We use a dynamic import but await it to ensure the store is ready before continuing
	 */
	(async () => {
		const { store } = await import('./store/index.js');
		const storeObject = {
			/**
			 * Dynamically retrieve the current data state
			 * We need to use a getter here to ensure we always get the latest state
			 * Otherwise, it would be a snapshot of the state at the time of assignment
			 */
			get data() {
				return store.getState().data;
			},
			/**
			 * Expose the useStore hook for operations
			 * This allows components to subscribe to store updates
			 * and get the latest state
			 */
			useStore: store.getState,
		};

		// Attach the store object to valioApp
		window.valioApp.store = storeObject;

		// Dispatch a custom event to signal that the store is ready
		window.dispatchEvent(
			new CustomEvent('valioStoreReady', {
				detail: storeObject,
			})
		);
	})();
}
